import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Space, Typography, Spin, Result, Button } from 'antd';
import { ArrowLeftOutlined, PrinterOutlined, ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import { dispatchPlanApi } from './dispatchPlanApi';

// ─────────────────────────────────────────────────────────────────
// Glass-i — Dispatch Plan VEHICLE LOADING SLIP viewer (Stage DISPATCH-LOADING-SLIP).
//
// The backend renders the A4 loading slip as a real, paginated PDF
// (GET /dispatch/plans/:id/loading-slip.pdf) via the generic Jasper engine.
// PDF gives EXACT, printer-safe page breaks — unlike browser HTML printing,
// which re-paginates the flat HTML and cuts rows across pages. The PDF blob is
// embedded in an iframe (native PDF view); Print prints it directly and
// Download saves it. The template lives in the drop-in dir
// (report-templates/reports/dispatch_loading_slip.jrxml) — click Reload to
// re-render after a design change.
// ─────────────────────────────────────────────────────────────────

async function errMessage(err) {
  const d = err && err.response && err.response.data;
  // With responseType:'blob', an error envelope arrives as a Blob — read it.
  if (d instanceof Blob) {
    try {
      const text = await d.text();
      try {
        const j = JSON.parse(text);
        if (j && j.error && j.error.message) return j.error.message;
      } catch { /* not JSON */ }
      if (text && text.length < 300) return text;
    } catch { /* ignore */ }
  }
  if (d && typeof d === 'object' && d.error && d.error.message) return d.error.message;
  return (err && err.message) || 'Could not load the loading slip.';
}

export default function DispatchLoadingSlipPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const urlRef = useRef(null);
  const wrapRef = useRef(null);

  const revoke = () => {
    if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
  };

  const load = useCallback(async () => {
    setLoading(true); setError(null); revoke(); setUrl(null);
    try {
      const blob = await dispatchPlanApi.loadingSlipPdf(id);
      const objUrl = URL.createObjectURL(blob);
      urlRef.current = objUrl;
      setUrl(objUrl);
    } catch (err) {
      setError(await errMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Load on mount / id change; revoke the object URL on unmount.
  useEffect(() => { load(); return revoke; }, [load]);

  const handlePrint = () => {
    const iframe = wrapRef.current && wrapRef.current.querySelector('iframe');
    if (iframe && iframe.contentWindow) { iframe.contentWindow.focus(); iframe.contentWindow.print(); }
  };

  const handleDownload = () => {
    if (!urlRef.current) return;
    const a = document.createElement('a');
    a.href = urlRef.current;
    a.download = `loading-slip-${id}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <Card
      title={
        <Space direction="vertical" size={0}>
          <Typography.Text strong>Vehicle Loading Slip</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>A4 PDF · grouped by work order · exact page breaks</Typography.Text>
        </Space>
      }
      extra={
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Back</Button>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Reload</Button>
          <Button icon={<DownloadOutlined />} onClick={handleDownload} disabled={!url}>Download</Button>
          <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint} disabled={!url}>Print</Button>
        </Space>
      }
      styles={{ body: { padding: 0 } }}
    >
      <div ref={wrapRef} style={{ height: 820, borderTop: '1px solid #f0f0f0' }}>
        {loading ? (
          <div style={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin tip="Rendering loading slip…" />
          </div>
        ) : error ? (
          <div style={{ padding: 24 }}>
            <Result status="error" title="Could not render the loading slip" subTitle={error}
              extra={<Space><Button onClick={() => navigate(-1)}>Back</Button><Button type="primary" onClick={load}>Retry</Button></Space>} />
          </div>
        ) : (
          <iframe
            title="Vehicle Loading Slip"
            src={url || ''}
            style={{ width: '100%', height: '100%', border: 'none', background: '#f0f2f5' }}
          />
        )}
      </div>
    </Card>
  );
}
